import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, Mail, Phone, BookOpen } from "lucide-react";
import Footer from "../components/Footer";

/**
 * Photo book order confirmation. The /photo-books order flow charges the
 * customer upfront via Razorpay; after a successful payment we route here
 * with the reference number + variant + amount in location.state so the
 * customer has a clear receipt + knows what happens next.
 */
const PhotoBookSuccessPage = () => {
  const { state } = useLocation();
  const ref = state?.reference_number;
  const amount = state?.amount;
  const variantName = state?.variant_name;
  const couponLabel = state?.coupon_label;
  const customerName = state?.customer_name;
  const email = state?.email;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="glass-header py-5">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex items-center justify-between">
          <Link
            to="/"
            className="serif text-2xl tracking-tight text-[#1A1A1A] font-semibold"
            data-testid="pb-success-brand"
          >
            theSnugglePrints
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-6 lg:px-10 py-16 md:py-24 w-full">
        <div className="text-center mb-10" data-testid="pb-success-page">
          <span className="inline-flex bg-[#5C6F4F] text-white rounded-full p-4 mb-5">
            <CheckCircle2 className="h-8 w-8" />
          </span>
          <p className="label-uppercase text-[#6B6B6B] mb-2">Payment received</p>
          <h1
            className="serif text-3xl sm:text-5xl text-[#1A1A1A] leading-tight mb-3"
            data-testid="pb-success-headline"
          >
            {customerName ? `Thank you, ${customerName}!` : "Thank you!"}
          </h1>
          <p className="text-[#3A3A3A] text-lg max-w-md mx-auto">
            Your photo book order is confirmed. Our team will design every page
            personally and keep you updated at every step.
          </p>
        </div>

        <div className="bg-card border border-[#EAE5DF] p-6 sm:p-8 mb-8">
          <h2 className="serif text-xl text-[#1A1A1A] mb-4">Order summary</h2>
          <dl className="text-sm space-y-3">
            {ref && (
              <div className="flex flex-wrap justify-between gap-2">
                <dt className="text-[#6B6B6B]">Reference</dt>
                <dd
                  className="font-mono text-[#1A1A1A]"
                  data-testid="pb-success-reference"
                >
                  {ref}
                </dd>
              </div>
            )}
            {variantName && (
              <div className="flex flex-wrap justify-between gap-2">
                <dt className="text-[#6B6B6B]">Item</dt>
                <dd className="text-[#1A1A1A]" data-testid="pb-success-variant">
                  {variantName}
                </dd>
              </div>
            )}
            {couponLabel && (
              <div className="flex flex-wrap justify-between gap-2">
                <dt className="text-[#6B6B6B]">Coupon applied</dt>
                <dd className="text-[#1A1A1A]" data-testid="pb-success-coupon">
                  {couponLabel}
                </dd>
              </div>
            )}
            {amount != null && (
              <div className="flex flex-wrap justify-between gap-2 pt-3 border-t border-[#EAE5DF]">
                <dt className="text-[#6B6B6B]">Amount paid</dt>
                <dd
                  className="serif text-2xl text-[#1A1A1A]"
                  data-testid="pb-success-amount"
                >
                  ₹{Number(amount).toLocaleString("en-IN")}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div className="bg-[#FAF6EF] border border-[#EAE5DF] p-6 sm:p-8 mb-8">
          <h2 className="serif text-xl text-[#1A1A1A] mb-3">What happens next</h2>
          <ol className="text-sm text-[#3A3A3A] space-y-3 list-decimal pl-5">
            <li>
              <strong>Within 12 hours</strong> — our team reaches out to collect
              your photos and any special notes (occasion, page count, theme).
            </li>
            <li>
              <strong>Design phase</strong> — we hand-design every page
              personally and share a preview for your approval.
            </li>
            <li>
              <strong>Shipped in 7–10 days</strong> — once approved, your photo
              book is printed and shipped to your door.
            </li>
          </ol>
          {email && (
            <p className="text-xs text-[#6B6B6B] mt-5">
              <Mail className="inline h-3 w-3 mr-1" />
              We&apos;ll write to{" "}
              <strong className="text-[#1A1A1A]">{email}</strong> shortly.
            </p>
          )}
          <p className="text-xs text-[#6B6B6B] mt-2">
            <Phone className="inline h-3 w-3 mr-1" />
            Questions? Reply to our email or DM us on Instagram —
            we&apos;re happy to help.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/account"
            className="inline-flex items-center gap-2 h-11 px-5 border border-[#1A1A1A] text-sm text-[#1A1A1A]"
            data-testid="pb-success-account-link"
          >
            View my orders
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 h-11 px-5 btn-accent text-sm"
            data-testid="pb-success-home-link"
          >
            <BookOpen className="h-4 w-4" />
            Back to shop
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PhotoBookSuccessPage;
